import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchUserById, fetchModulesPermissions, fetchUserPermissions } from '../../../store/slices/adminSlice';

const SubAdminDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedUser: user, loading, error, modulesPermissions, userPermissions } = useAppSelector((state) => state.admin);
  const { countries } = useAppSelector((state) => state.utility);

  useEffect(() => {
    if (id) {
      dispatch(fetchUserById(id));
      dispatch(fetchModulesPermissions());
      dispatch(fetchUserPermissions(id));
    }
  }, [id, dispatch]);

  // Debug: Log user data when it changes
  useEffect(() => {
    if (userPermissions) {
      console.log('User permissions updated:', userPermissions);
      console.log('Permission IDs:', userPermissions.permission_ids);
    }
  }, [userPermissions]);

  // Convert string to Title Case
  const toTitleCase = (str) => {
    if (!str) return '';

    const specialCases = {
      'dataexport': 'Data Export',
      'data export': 'Data Export',
      'jetsetter': 'JetSetter',
      'jet_setter': 'Jet Setter',
      'airliner': 'Airliner',
      'airliners': 'Airliner',
      'subadmin': 'SubAdmin',
      'sub_admin': 'Sub Admin',
      'venders': 'Vendor',
      'vender': 'Vendor',
      'cms': 'CMS',
      'notifications': 'Messages',
      'notification': 'Messages',
      'contract_management': 'Contract Management',
      'account_settings': 'Account Settings',
      'user_management': 'User Management'
    };

    const normalized = str.toLowerCase().trim().replace(/-/g, '_');
    if (specialCases[normalized]) {
      return specialCases[normalized];
    }

    return str
      .toLowerCase()
      .trim()
      .split(/[\s_-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get user's permissions
  const getUserPermissions = () => {
    if (!user || !user.permissions || !modulesPermissions) return {};

    const permissionMap = {};
    modulesPermissions.forEach(module => {
      const modulePerms = module.permissions.filter(p =>
        user.permissions.includes(p.id)
      );
      if (modulePerms.length > 0) {
        permissionMap[module.name] = modulePerms.map(p => p.name);
      }
    });
    return permissionMap;
  };

  // Check if user has a specific permission for a module
  const hasPermission = (module, permissionName) => {
    // Find if this permission type exists on the module at all
    const permExists = module.permissions.find(
      p => p.name.toLowerCase() === permissionName.toLowerCase()
    );

    // If this permission type doesn't exist on the module, render nothing
    if (!permExists) return '';

    const label = permissionName.toLowerCase() === 'export'
      ? 'Data Export'
      : toTitleCase(permissionName);

    // Use userPermissions from state instead of user.permissions
    if (!userPermissions) {
      return <span className="text-gray-300">{label}</span>;
    }

    const permissionIds = userPermissions.permission_ids || [];
    const isGranted = permissionIds.includes(permExists.id);

    if (isGranted) {
      return label;
    }

    // Permission exists on module but not granted — show in light gray
    return <span className="text-gray-300">{label}</span>;
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <i className="fas fa-circle-notch fa-spin text-2xl text-yellow-500"></i>
        <p className="mt-2 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-12 text-center text-red-500">Error: {error}</div>;
  }

  if (!user) {
    return <div className="p-12 text-center">SubAdmin not found</div>;
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}`;
  };

  // Get country phone code by country_id or from user's phone_code field
  const getPhoneCode = (countryId, userPhoneCode) => {
    // First try to get from country_id
    if (countries && countryId) {
      const country = countries.find(c => c.id === parseInt(countryId));
      if (country?.phone_code) return country.phone_code;
    }
    // Fallback to user's phone_code field if available
    return userPhoneCode || '';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">SubAdmin Details</h1>
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-400 rounded hover:bg-yellow-500 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
          <span className="font-semibold">Back</span>
        </button>
      </div>

      {/* SubAdmin Details Card */}
      <div className="bg-white rounded shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Profile Image */}
          <div className="w-full md:w-[150px] flex-shrink-0">
            <div className="w-full h-[130px] bg-gray-200 rounded flex flex-col items-center justify-center text-gray-400">
              {user.profileImage ? (
                <img
                  src={user.profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <>
                  <i className="far fa-images text-3xl text-gray-300 mb-1"></i>
                  <span className="text-xs font-medium text-gray-400">No Image Available</span>
                </>
              )}
            </div>
          </div>

          {/* Details Grid */}
          <div className="flex-1">
            {/* Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-t border-b border-gray-200 py-3">
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">First Name</span>
                <span className="text-gray-900 text-sm font-semibold">{user.first_name || 'SUBADMIN'}</span>
              </div>
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">Last Name</span>
                <span className="text-gray-900 text-sm font-semibold">{user.last_name || 'THREE'}</span>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-gray-200 py-3">
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">Email</span>
                <span className="text-gray-900 text-sm font-semibold truncate">{user.email || 'subthree@mailinator.com'}</span>
              </div>
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">Mobile Number</span>
                <span className="text-gray-900 text-sm font-semibold">
                  {user.mobile_number
                    ? `${getPhoneCode(user.country_id, user.phone_code)} ${user.mobile_number}`.trim()
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-gray-200 py-3">
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">User ID</span>
                <span className="text-gray-900 text-sm font-semibold">{user.user_id || 'SAA'}</span>
              </div>
              <div className="flex items-center justify-between px-4">
                <span className="text-gray-500 text-sm">Date/Time Created</span>
                <span className="text-gray-900 text-sm font-semibold">{formatDate(user.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions Section */}
      <div className="bg-white rounded shadow-sm p-6">
        <h2 className="text-base font-semibold mb-4">Permissions</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {modulesPermissions && modulesPermissions.length > 0 ? (
                modulesPermissions.map((module) => (
                  <tr key={module.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-700 font-semibold" style={{ textTransform: 'none' }}>{toTitleCase(module.name)}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'index')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'export')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'add')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'edit')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'view')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'archive')}</td>
                    <td className="p-3 text-center text-sm text-gray-600 font-semibold" style={{ textTransform: 'none' }}>{hasPermission(module, 'delete')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-6 text-center text-gray-500">
                    {userPermissions && userPermissions.permission_ids && userPermissions.permission_ids.length === 0
                      ? 'NO PERMISSIONS ASSIGNED'
                      : 'Loading permissions...'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubAdminDetails;
